import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error?.response?.status === 402) {
      toast.error(error.response.data?.detail || "You're out of credits.", {
        action: { label: 'Upgrade', onClick: () => { window.location.href = '/app/upgrade'; } },
      });
    }
    return Promise.reject(error);
  }
);

// Short-lived, read-only media token for header-less <img>/<a> fetches.
// Replaces putting the long-lived session token in the URL.
let mediaToken = localStorage.getItem('media_token') || '';
export const refreshMediaToken = async () => {
  try {
    const res = await api.get('/media/token');
    mediaToken = res.data.token;
    localStorage.setItem('media_token', mediaToken);
  } catch (e) { /* keep existing token */ }
  return mediaToken;
};
// Keep a fresh token while signed in (token lives ~30 min; refresh every 25).
if (typeof window !== 'undefined') {
  const tick = () => { if (localStorage.getItem('session_token')) refreshMediaToken(); };
  tick();
  setInterval(tick, 25 * 60 * 1000);
}

// Build a media URL usable directly in <img src> / <a href>.
export const mediaSrc = (mediaId) => {
  if (!mediaId) return null;
  return `${API}/media/${mediaId}/content?mt=${encodeURIComponent(mediaToken)}`;
};

// Upload any file to private object storage. Returns the media record.
export const uploadMedia = async (file, context = 'general', refId = '') => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('context', context);
  if (refId) fd.append('ref_id', refId);
  const res = await api.post('/media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return res.data;
};

// Safely navigate to an external URL returned by our own API (checkout / OAuth).
// Defense-in-depth: only allow https:// to a known provider host before redirecting.
const ALLOWED_REDIRECT_HOSTS = [
  'checkout.stripe.com', 'billing.stripe.com', 'connect.stripe.com',
  'accounts.google.com', 'login.microsoftonline.com',
];
export const safeExternalRedirect = (rawUrl) => {
  try {
    const u = new URL(rawUrl);
    const ok = u.protocol === 'https:' && ALLOWED_REDIRECT_HOSTS.some((h) => u.hostname === h || u.hostname.endsWith('.' + h));
    if (!ok) { toast.error('Blocked an unexpected redirect.'); return false; }
    window.location.href = u.toString();
    return true;
  } catch (e) {
    toast.error('Invalid redirect URL.');
    return false;
  }
};

export default api;
